<template>
  <div class="task-details" @click="stopBubble">
    <!-- warranty_program -->
    <div v-if="task.kind === 'warranty_program'">
      <div
        v-if="task.description"
        class="description flex-col md:flex-row gap-3"
      >
        <div class="mr-3 flex-1">{{ task.description }}</div>
        <div class="flex-1">
          {{ task.warranty }}
        </div>
      </div>
      <div class="member-submit-cont w-full md:w-4/12">
        <div
          class="add-task-button whitespace-nowrap w-full"
          @click="handleClick"
        >
          {{ adding ? "Adding Program..." : "Add Warranty Program" }}
        </div>
      </div>
      <div v-if="taskPrice" class="member-warranty-override-cont mt-2">
        <div>
          <label>
            <input v-model="overridePrice" type="checkbox" />
            Override Price
          </label>
          <input
            v-model="displayPrice"
            type="text"
            :disabled="!overridePrice"
            class="mt-2 ml-2 p-2 border rounded"
          />
        </div>
      </div>
    </div>

    <!-- membership_program -->
    <div v-else-if="task.kind === 'membership_program'">
      <div
        v-if="task.description"
        class="description flex-col md:flex-row gap-3"
      >
        <div class="mr-3 flex-1">{{ task.description }}</div>
        <div class="flex-1">
          {{ coveredSystemsDescription }}
        </div>
      </div>
      <div class="member-submit-cont w-full md:w-4/12">
        <div
          class="add-task-button whitespace-nowrap w-full"
          @click="handleClick"
        >
          {{ adding ? "Adding Program..." : "Add Membership Program" }}
        </div>
      </div>
      <div v-if="taskPrice" class="member-warranty-override-cont mt-2">
        <div>
          <label>
            <input v-model="overridePrice" type="checkbox" />
            Override Price
          </label>
          <input
            v-model="displayPrice"
            type="text"
            :disabled="!overridePrice"
            class="mt-2 ml-2 p-2 border rounded"
          />
        </div>
      </div>
    </div>

    <!-- Everything else -->
    <div v-else class="flex-col md:flex-row">
      <div class="warranty">
        <span class="font-bold">Warranty:&nbsp;</span>
        <span>{{ task.warranty || "Not provided" }}</span>
      </div>
      <div v-if="task.description" class="description">
        <div>{{ task.description }}</div>
      </div>
      <div class="upgrades-addons-cont mx-0 md:mx-auto w-full md:w-10/12">
        <Addons
          v-if="showAddons"
          title="Add-ons"
          :tasks="task.addons"
          :selected="addons"
          @update-addons="updateAddons"
        />
        <div class="add-task-button w-full md:w-4/12" @click="handleClick">
          {{ submitText }}
        </div>
        <div v-if="taskPrice" class="mt-2">
          <div>
            <label>
              <input v-model="overridePrice" type="checkbox" />
              Override Price
            </label>
            <input
              v-model="displayPrice"
              type="text"
              :disabled="!overridePrice"
              class="mt-2 ml-2 p-2 border rounded"
            />
          </div>
        </div>
      </div>
      <div class="links">
        <LinkCollection
          v-if="showUpgrades"
          title="Upgrades"
          :links="upgradeLinks"
          empty-text="No recommendations available"
          class="collection"
        />
        <RecentlyViewed
          class="recently-viewed collection"
          :current-task="task"
        />
      </div>
    </div>
  </div>
</template>

<script>
import Addons from "serviceshift-ui/components/TaskPicker/Addons.vue";
import LinkCollection from "serviceshift-ui/components/TaskPicker/LinkCollection.vue";
import RecentlyViewed from "serviceshift-ui/components/TaskPicker/RecentlyViewed.vue";
import currency from "serviceshift-ui/shared/src/filters/currency";
import EventBus from "serviceshift-ui/shared/src/lib/event_bus";

export default {
  components: {
    Addons,
    LinkCollection,
    RecentlyViewed
  },
  props: {
    showAddons: {
      type: Boolean,
      default: true
    },
    showUpgrades: {
      type: Boolean,
      default: true
    },
    task: {
      type: Object,
      default: null
    },
    replaceMode: {
      type: Boolean,
      default: false
    },
    taskPrice: {
      type: Number,
      default: null
    }
  },
  data() {
    return {
      addons: [],
      adding: false,
      overridePrice: false,
      taskOverridePrice:
        this.taskPrice != null ? this.taskPrice.toFixed(2) : "",
      localTask: this.task ? { ...this.task } : {}
    };
  },
  computed: {
    submitText() {
      if (this.adding) {
        return this.replaceMode ? "Replacing..." : "Adding...";
      }
      const action = this.replaceMode ? "Replace" : "Add";
      let text = `${action} Item`;
      if (this.addons.length) {
        const plural = this.addons.length > 1;
        text += ` + ${this.addons.length} Add-on${plural ? "s" : ""}`;
      }
      return text;
    },
    upgradeLinks() {
      if (!this.task) {
        return [];
      }
      return this.task.upgrades.map((upgrade) => ({
        label: upgrade.name,
        eventName: "upgrade-click",
        data: upgrade
      }));
    },
    coveredSystemsDescription() {
      const membershipPrices = this.task.prices.find(
        (membership) => membership.membership_program_id === this.task.id
      );
      if (membershipPrices) {
        const price = `$${(
          membershipPrices.additional_unit_price_cents / 100
        ).toFixed(2)}`;
        return `Includes 1 system\n + ${price} for each additional system`;
      }
      return "";
    },
    displayPrice: {
      get() {
        if (!this.overridePrice && this.taskPrice != null) {
          return currency(this.taskPrice);
        }
        return this.taskOverridePrice;
      },
      set(value) {
        this.taskOverridePrice = value;
      }
    }
  },
  watch: {
    taskPrice(newVal) {
      if (!this.overridePrice) {
        this.taskOverridePrice = newVal ? String(newVal) : "";
      }
    }
  },
  methods: {
    handleClick() {
      if (!this.adding) {
        const allItems = [this.localTask].concat(this.addons);
        this.adding = true;
        // Only an explicit override may carry a price — the backend persists
        // any submitted price as a manual price adjustment, overriding the
        // price sheet. Without an override the backend prices the line item
        // from the quote's membership program.
        if (this.overridePrice) {
          const strippedPrice = this.taskOverridePrice.replace(/[^0-9.]/g, "");
          this.localTask.unit_price_cents = Math.round(
            Number(strippedPrice) * 100
          );
        } else {
          delete this.localTask.unit_price_cents;
        }
        const eventName = this.replaceMode ? "replace-task" : "add-task";
        EventBus.$emit(eventName, allItems, this.localTask, this.addons, () => {
          this.adding = false;
        });
      }
    },
    stopBubble(e) {
      e.stopPropagation();
    },
    updateAddons(e, addon) {
      if (e.target.checked) {
        this.addons.push(addon);
      } else {
        this.addons = this.addons.filter((a) => a.id !== addon.id);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.task-details {
  padding: 20px;
  background-color: #fcfcfc;
  border-bottom: var(--border);
}
.warranty {
  margin-bottom: 0.75em;
}
.description {
  white-space: pre-line;
  display: flex;
  justify-content: space-between;
}
.warranty,
.description,
.links {
  font-size: 0.9em;
}
.upgrades-addons-cont {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 1em auto;
  width: 90%;
  padding: 10px 0;
}
.upgrade-card {
  margin-right: 2em;
}
.add-task-button {
  @apply p-2.5 rounded-lg bg-primary-700;
  margin-top: 1em;
  background-color: var(--primary-color);
  box-shadow: var(--box-shadow) !important;
  color: #fff;
  text-align: center;
  font-size: 0.9em;
  &:hover,
  &:focus,
  &:active {
    background-color: #1155dd;
  }
}

.member-submit-cont {
  display: block;
  width: 40%;
  margin: 1.5em auto 0 auto;
}
.member-warranty-override-cont {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 1em;
}
.links {
  display: flex;
  align-items: stretch;
  border-top: 1px solid var(--primary-light-accent-color);
  padding-top: 20px;
  .collection {
    width: 50%;
  }
}
</style>
